/**
 * One fetch of the staffing read set, shared by every tab in the module.
 *
 * Held at the LAYOUT rather than in each page because the five reads are a set:
 * a book cannot be tiered without its config and cannot be distributed without
 * its slots, so per-page fetching would only multiply the half-loaded states a
 * user can see. It also means switching Overview ↔ Portfolios re-renders instead
 * of re-fetching, which is what makes a threshold slider feel live.
 *
 * The CRM module's plain useEffect + apiFetch pattern, not react-query: these
 * routes sit behind CrmModuleShell alongside the other cm-api pages, and there
 * is no QueryClientProvider over this part of the tree today.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '@/features/crm/lib/api-fetch';
import { fetchStaffingReadSet, type StaffingReadSet } from './api';
import {
  EMPTY_SCENARIO,
  isScenarioActive,
  scenarioChangeCount,
  type Scenario,
} from './lib/scenario';

export interface StaffingDataState {
  read: StaffingReadSet | null;
  loading: boolean;
  error: string | null;
  /**
   * Which subset of the CRM the book covers. cm-api REQUIRES the caller to say
   * (there is no default) because the reconciliation that would settle it has
   * not run — see #1802. So it is a visible control, not a hidden constant.
   */
  includeInactive: boolean;
  setIncludeInactive: (next: boolean) => void;
  reload: () => void;

  /**
   * The "what if" overlay, held HERE rather than in a page.
   *
   * A scenario spans tabs by nature: reshaping the bands is a question about
   * cost (Overview) and about who ends up with whom (Portfolios), and the whole
   * point is to see both. Page-local state would reset the moment you went to
   * look at the other half of the answer.
   */
  scenario: Scenario;
  setScenario: (next: Scenario | ((prev: Scenario) => Scenario)) => void;
  resetScenario: () => void;
  /** Anything overridden at all. */
  scenarioActive: boolean;
  /** How many things differ from what is stored — 0 even if a move was undone. */
  scenarioChanges: number;
}

const StaffingDataContext = createContext<StaffingDataState | null>(null);

export function StaffingDataProvider({ children }: { children: ReactNode }) {
  const [read, setRead] = useState<StaffingReadSet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [scenario, setScenario] = useState<Scenario>(EMPTY_SCENARIO);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const resetScenario = useCallback(() => setScenario(EMPTY_SCENARIO), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchStaffingReadSet({ includeInactive })
      .then((next) => {
        if (cancelled) return;
        setRead(next);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 422 is the interesting one: the request was fine and the CRM data
        // cannot answer it (two advisors sharing one key, a non-finite spend).
        // cm-api names the offenders in the body; show that rather than a status.
        const body =
          err instanceof ApiError && err.body && typeof err.body === 'object'
            ? (err.body as { error?: string; remedy?: string })
            : null;
        setError(
          [body?.error, body?.remedy].filter(Boolean).join(' — ') ||
            (err instanceof Error
              ? err.message
              : 'Failed to load staffing data'),
        );
        setRead(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [includeInactive, nonce]);

  // A scenario is a diff over a specific book. Changing which customers the book
  // covers changes what the diff MEANS — a pin to someone who is not in the new
  // read set is a pin to nobody — so the overlay is dropped rather than silently
  // reinterpreted against different data.
  useEffect(() => {
    setScenario(EMPTY_SCENARIO);
  }, [includeInactive]);

  const stored = read
    ? {
        tiers: read.config.config?.tiers ?? [],
        slots: read.slots.slots,
        pins: read.pins.pins,
        balanceBy: read.config.config?.balanceBy ?? 'count',
      }
    : null;

  return (
    <StaffingDataContext.Provider
      value={{
        read,
        loading,
        error,
        includeInactive,
        setIncludeInactive,
        reload,
        scenario,
        setScenario,
        resetScenario,
        scenarioActive: isScenarioActive(scenario),
        scenarioChanges: stored ? scenarioChangeCount(scenario, stored) : 0,
      }}
    >
      {children}
    </StaffingDataContext.Provider>
  );
}

export function useStaffingData(): StaffingDataState {
  const ctx = useContext(StaffingDataContext);
  if (!ctx) {
    throw new Error(
      'useStaffingData must be used inside <StaffingDataProvider> (the module layout provides it)',
    );
  }
  return ctx;
}
